/**
 * AccountsExecDashboard — Accounts Executive (AP/AR clerk).
 *
 * Ported from `AccountsExecDashboard` in aura-exec.jsx.
 * Invoice-to-payment match queue with AI confidence bars + voucher shortcut +
 * GSTR-1/3B draft lines + TDS certificate queue.
 * (L202/N2, 2026-07-16 — the expense-approvals section was removed with the
 *  retired `Expense` vendor ledger; reimbursements live on HrExpenseClaim.)
 *
 * UI-only. Tenant-safe.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Download, ClipboardCheck, Sparkles, Users, FileText } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { safeGet, formatINRCompact, formatNum, todayLabel, makeCreditBurn } from './_execShared';

// ─── Types ────────────────────────────────────────────────────────────────

interface MatchRow {
  id: string;
  number?: string;
  reference?: string;
  amount?: number;
  candidateInvoice?: string;
  candidate?: string;
  confidence?: number;
  conf?: number;
  flagged?: boolean;
  flag?: boolean;
}

interface GstDraftLine {
  id: string;
  invoiceNumber?: string;
  hsnCode?: string;
  amount?: number;
  warning?: string;
}

// ─── Atoms ────────────────────────────────────────────────────────────────

function Skeleton({ h = 60 }: { h?: number }) {
  return (
    <div
      style={{
        height: h,
        background: 'var(--ivory-2)',
        borderRadius: 6,
        animation: 'pulse 1.5s ease-in-out infinite',
      }}
    />
  );
}

function NoData({ label = 'No data yet' }: { label?: string }) {
  return <div style={{ fontSize: 12, color: 'var(--ink-3)', padding: '8px 0' }}>{label}</div>;
}

// L202/N2 (2026-07-16) — the `Avatar` atom was removed with the expense-approvals
// table (its only consumer); the retired `Expense` ledger had 0 rows ever.

function Chip({
  children,
  color = 'var(--ink-3)',
  bg = 'var(--ivory-2)',
}: {
  children: React.ReactNode;
  color?: string;
  bg?: string;
}) {
  return (
    <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, background: bg, color, fontWeight: 500 }}>
      {children}
    </span>
  );
}

function ConfidenceBar({ value }: { value: number }) {
  const color =
    value >= 95 ? 'var(--forest-600)' : value >= 80 ? 'var(--saffron-600)' : 'var(--danger, #B8563B)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ width: 40, height: 4, background: 'var(--ivory-3)', borderRadius: 2 }}>
        <div
          style={{
            width: `${Math.max(0, Math.min(100, value))}%`,
            height: '100%',
            background: color,
            borderRadius: 2,
          }}
        />
      </div>
      <span className="num" style={{ fontSize: 10, color }}>
        {value}%
      </span>
    </div>
  );
}

function KpiCard({
  label,
  value,
  subtitle,
  color,
}: {
  label: string;
  value: string;
  subtitle?: string;
  color?: string;
}) {
  return (
    <div className="card" style={{ padding: 14 }}>
      <div className="kpi-label">{label}</div>
      <div className="num-display" style={{ fontSize: 22, marginTop: 2, color }}>
        {value}
      </div>
      {subtitle && <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{subtitle}</div>}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────

export function AccountsExecDashboard({
  onSeeTeam,
}: {
  onSeeTeam?: () => void;
} = {}) {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const firstName = user?.firstName ?? 'there';

  const [matches, setMatches] = useState<MatchRow[]>([]);
  const [matchesLoading, setMatchesLoading] = useState(true);
  const [matchesErr, setMatchesErr] = useState<string | null>(null);

  const [gstDrafts, setGstDrafts] = useState<GstDraftLine[]>([]);
  const [gstLoading, setGstLoading] = useState(true);

  const [tdsPending, setTdsPending] = useState<number | null>(null);

  const burn = makeCreditBurn();

  useEffect(() => {
    safeGet<{ items?: MatchRow[] } | MatchRow[]>('/payments/unmatched').then((r) => {
      const raw = r.data;
      const parsed = Array.isArray(raw) ? raw : (raw as { items?: MatchRow[] } | null)?.items ?? [];
      setMatches(parsed);
      setMatchesErr(r.error);
      setMatchesLoading(false);
    });

    safeGet<{ items?: GstDraftLine[] } | GstDraftLine[]>('/gst/filing/draft-lines').then((r) => {
      const raw = r.data;
      const parsed = Array.isArray(raw) ? raw : (raw as { items?: GstDraftLine[] } | null)?.items ?? [];
      setGstDrafts(parsed);
      setGstLoading(false);
    });

    safeGet<{ count?: number; pending?: number }>('/tds/certificates?status=pending').then((r) => {
      setTdsPending(r.data?.count ?? r.data?.pending ?? null);
    });
  }, []);

  const getConf = (m: MatchRow) => m.confidence ?? m.conf ?? 0;
  const getCandidate = (m: MatchRow) => m.candidateInvoice ?? m.candidate ?? '—';

  const gstWarnings = gstDrafts.filter((g) => g.warning).length;

  return (
    <div
      className="aura-root"
      style={{ padding: 20, background: 'var(--ivory-0)', minHeight: '100%' }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: 14, gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 11,
              color: 'var(--ink-3)',
              textTransform: 'uppercase',
              letterSpacing: 0.08,
              marginBottom: 3,
            }}
          >
            Accounts Executive · AP/AR desk
          </div>
          <h2 style={{ fontFamily: 'var(--ff-serif)', fontSize: 24, fontWeight: 500 }}>
            Namaste, {firstName}
          </h2>
          <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            {todayLabel()} · {matches.length} matches
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {onSeeTeam && (
          <button
            className="btn btn-outline btn-sm"
            onClick={onSeeTeam}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Users size={12} /> See team view
          </button>
        )}
      </div>

      {/* KPI strip */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 10,
          marginBottom: 14,
        }}
      >
        <KpiCard
          label="Match queue"
          value={formatNum(matches.length)}
          subtitle="Unmatched payments"
          color={matches.length > 0 ? 'var(--saffron-600)' : 'var(--forest-600)'}
        />
        <KpiCard
          label="GSTR-1 drafts"
          value={formatNum(gstDrafts.length)}
          subtitle={gstWarnings > 0 ? `${gstWarnings} warnings` : 'Clean'}
        />
        <KpiCard
          label="TDS certs pending"
          value={tdsPending != null ? formatNum(tdsPending) : '—'}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 14 }}>
        <div>
          {/* Match queue */}
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <h3 style={{ fontSize: 13, fontWeight: 500 }}>Invoice ↔ payment match queue</h3>
            <span style={{ marginLeft: 8 }}>
              <Chip>{matches.length}</Chip>
            </span>
            <div style={{ flex: 1 }} />
            <button
              className="btn btn-ai btn-sm"
              onClick={() => burn(12, 'auto_match_all')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <Sparkles size={11} /> Auto-match all · 12 cr
            </button>
          </div>
          {matchesLoading ? (
            <Skeleton h={180} />
          ) : matchesErr ? (
            <div className="card" style={{ padding: 14, marginBottom: 18 }}>
              <NoData label="Match queue unavailable" />
            </div>
          ) : matches.length === 0 ? (
            <div className="card" style={{ padding: 14, marginBottom: 18 }}>
              <NoData label="All payments matched — queue clear" />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 18 }}>
              {matches.slice(0, 10).map((m) => {
                const conf = getConf(m);
                const accent = m.flagged || m.flag
                  ? 'var(--danger, #B8563B)'
                  : conf >= 95
                  ? 'var(--forest-600)'
                  : conf >= 80
                  ? 'var(--saffron-600)'
                  : 'transparent';
                return (
                  <div
                    key={m.id}
                    className="card"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: 10,
                      borderLeft: `3px solid ${accent}`,
                    }}
                  >
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 6,
                        background: 'var(--ivory-2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <FileText size={16} color="var(--ink-2)" />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>
                        {m.number ?? m.id}
                        <span className="num" style={{ fontWeight: 400, color: 'var(--ink-3)' }}>
                          {' '}
                          · {formatINRCompact(m.amount ?? 0)}
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: 'var(--ink-3)',
                          display: 'flex',
                          gap: 6,
                          marginTop: 2,
                        }}
                      >
                        <span>{m.reference ?? '—'}</span>
                        <span>·</span>
                        <span>→ {getCandidate(m)}</span>
                      </div>
                    </div>
                    {conf > 0 ? (
                      <ConfidenceBar value={conf} />
                    ) : (
                      <Chip color="var(--danger, #B8563B)" bg="var(--danger-bg, #FBEAE6)">
                        No match
                      </Chip>
                    )}
                    {/* Apr 27 (audit P0/P1) — wired Review/Apply/Find to payments tab. */}
                    <button
                      className="btn btn-sm btn-outline"
                      onClick={() => navigate(`/payments?match=${encodeURIComponent(m.id)}`)}
                    >
                      Review
                    </button>
                    {conf >= 95 ? (
                      <button
                        className="btn btn-sm"
                        onClick={() => navigate(`/payments?apply=${encodeURIComponent(m.id)}`)}
                        style={{
                          background: 'var(--ink-0)',
                          color: 'var(--ivory-0)',
                          border: '1px solid var(--ink-0)',
                        }}
                      >
                        Apply
                      </button>
                    ) : (
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => navigate('/payments?tab=unmatched')}
                      >
                        Find
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* L202/N2 (2026-07-16) — "Expense approvals" card REMOVED. It read the
              retired `/expenses` route and deep-linked Approve/Reject into
              `/expenses?approve=`/`?reject=` (both routes deleted). The `Expense`
              vendor ledger had 0 rows ever, so this card only ever rendered its
              empty state. Employee reimbursements live on the HrExpenseClaim rail. */}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* GSTR warning card */}
          <div
            className="card"
            style={{
              padding: 14,
              background: 'linear-gradient(180deg, var(--saffron-50), var(--saffron-100))',
              border: '1px solid var(--saffron-100)',
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: 'var(--saffron-600)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: 0.06,
                marginBottom: 6,
              }}
            >
              ⚠ GSTR-1 draft review
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.5 }}>
              {gstLoading
                ? 'Loading…'
                : gstDrafts.length === 0
                ? 'No draft lines pending review'
                : `${gstDrafts.length} draft line${gstDrafts.length === 1 ? '' : 's'} need review${
                    gstWarnings > 0 ? ` · AI flagged ${gstWarnings} warning${gstWarnings === 1 ? '' : 's'}` : ''
                  }`}
            </div>
            {/* Apr 27 (audit P1) — Review drafts → GST filing dashboard. */}
            <button
              className="btn btn-sm"
              onClick={() => navigate('/finance/gst-filing')}
              style={{ marginTop: 10, width: '100%', background: 'var(--ivory-1)' }}
            >
              Review drafts
            </button>
          </div>

          {/* Shortcuts */}
          <div className="card" style={{ padding: 14 }}>
            <div className="kpi-label" style={{ marginBottom: 10 }}>
              Shortcuts
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {/* Apr 27 (audit P1) — wired all 3 shortcuts to their owning routes. */}
              <button
                className="btn btn-outline btn-sm"
                onClick={() => navigate('/accounting?tab=vouchers&action=new')}
                style={{ justifyContent: 'flex-start', display: 'inline-flex', gap: 6 }}
              >
                <Plus size={11} /> New voucher
              </button>
              <button
                className="btn btn-outline btn-sm"
                onClick={() => navigate('/accounting?tab=bank-recon&action=import')}
                style={{ justifyContent: 'flex-start', display: 'inline-flex', gap: 6 }}
              >
                <Download size={11} /> Bank statement import
              </button>
              <button
                className="btn btn-outline btn-sm"
                onClick={() => navigate('/finance/gst-filing?tab=tds')}
                style={{ justifyContent: 'flex-start', display: 'inline-flex', gap: 6 }}
              >
                <ClipboardCheck size={11} /> TDS 26Q queue
              </button>
            </div>
          </div>

          {/* AI digest */}
          <div
            className="card"
            style={{
              padding: 14,
              background: 'var(--ivory-1)',
              border: '1px dashed var(--saffron-100)',
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--saffron-600)',
                textTransform: 'uppercase',
                letterSpacing: 0.06,
                marginBottom: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Sparkles size={12} /> Today's digest
            </div>
            <ul
              style={{
                margin: 0,
                paddingLeft: 16,
                fontSize: 12,
                color: 'var(--ink-2)',
                lineHeight: 1.6,
              }}
            >
              <li>
                {matches.length} payment{matches.length === 1 ? '' : 's'} still unmatched
              </li>
              <li>
                {tdsPending ?? 0} TDS certificate{(tdsPending ?? 0) === 1 ? '' : 's'} ready to dispatch
              </li>
            </ul>
            <button
              className="btn btn-ai btn-sm"
              style={{ width: '100%', marginTop: 10 }}
              onClick={() => burn(5, 'ai_digest_refresh')}
            >
              <Sparkles size={11} style={{ marginRight: 6 }} /> Refresh · 5 cr
            </button>
          </div>
        </div>
      </div>

      <style>{`@keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
    </div>
  );
}

export default AccountsExecDashboard;
